import { useState, type ReactNode } from 'react';
import {
  Button,
  Combobox,
  Group,
  InputBase,
  Loader,
  ScrollArea,
  Text,
  useCombobox,
  type MantineSize,
} from '@mantine/core';

export interface ServerSelectQuery<T> {
  data: { items: T[]; total: number } | undefined;
  isPending: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}

export interface ServerSelectProps<T> {
  label?: ReactNode;
  'aria-label'?: string;
  description?: ReactNode;
  placeholder?: string;
  required?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  size?: MantineSize;
  w?: number | string;
  value: string | null;
  /**
   * Label for `value` when it isn't among the loaded options (it was chosen earlier, or came
   * from the URL): use the loaded entity's own label, or a single fetch.
   */
  valueLabel?: string | null;
  onChange: (value: string | null, item: T | null) => void;
  /** The search box text; the owner debounces it into the server query. */
  search: string;
  onSearchChange: (search: string) => void;
  /** Called when the dropdown opens or closes (load options only once someone looks). */
  onOpenChange?: (opened: boolean) => void;
  query: ServerSelectQuery<T>;
  getValue: (item: T) => string;
  getLabel: (item: T) => string;
  renderOption?: (item: T) => ReactNode;
  /** Hide options (e.g. ones already added). */
  exclude?: (item: T) => boolean;
  nothingFound?: string;
  /** Plural noun for the count, e.g. "creators". */
  noun: string;
}

/**
 * A searchable select backed by a paged server list: the search goes to the API (`q`), the
 * dropdown shows "Showing X of Y" and loads more pages on demand.
 */
export function ServerSelect<T>({
  label,
  'aria-label': ariaLabel,
  description,
  placeholder,
  required,
  clearable,
  disabled,
  error,
  size,
  w,
  value,
  valueLabel,
  onChange,
  search,
  onSearchChange,
  onOpenChange,
  query,
  getValue,
  getLabel,
  renderOption,
  exclude,
  nothingFound = 'Nothing found',
  noun,
}: ServerSelectProps<T>) {
  const [picked, setPicked] = useState<{ value: string; label: string } | null>(null);
  const combobox = useCombobox({
    onDropdownOpen: () => onOpenChange?.(true),
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onSearchChange('');
      onOpenChange?.(false);
    },
  });

  const items = query.data?.items ?? [];
  const shown = exclude ? items.filter((i) => !exclude(i)) : items;
  const fromList = value ? items.find((i) => getValue(i) === value) : undefined;
  const selectedLabel = !value
    ? ''
    : picked?.value === value
      ? picked.label
      : fromList
        ? getLabel(fromList)
        : (valueLabel ?? '');

  const choose = (v: string) => {
    const item = items.find((i) => getValue(i) === v) ?? null;
    if (item) setPicked({ value: v, label: getLabel(item) });
    onChange(v, item);
    combobox.closeDropdown();
  };

  const clear = () => {
    setPicked(null);
    onChange(null, null);
  };

  return (
    <Combobox store={combobox} onOptionSubmit={choose} disabled={disabled} size={size}>
      <Combobox.Target>
        <InputBase
          label={label}
          aria-label={ariaLabel}
          description={description}
          required={required}
          disabled={disabled}
          error={error}
          size={size}
          w={w}
          placeholder={combobox.dropdownOpened && selectedLabel ? selectedLabel : placeholder}
          value={combobox.dropdownOpened ? search : selectedLabel}
          onChange={(e) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            onSearchChange(e.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            clearable && value && !disabled ? (
              <Combobox.ClearButton onClear={clear} aria-label="Clear" />
            ) : query.isFetching && combobox.dropdownOpened ? (
              <Loader size={14} />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={260} type="scroll">
            {query.isPending ? (
              <Combobox.Empty>Loading…</Combobox.Empty>
            ) : shown.length === 0 ? (
              <Combobox.Empty>{nothingFound}</Combobox.Empty>
            ) : (
              shown.map((item) => {
                const v = getValue(item);
                return (
                  <Combobox.Option value={v} key={v} active={v === value}>
                    {renderOption ? renderOption(item) : getLabel(item)}
                  </Combobox.Option>
                );
              })
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
        {query.data && query.data.total > 0 && (
          <Combobox.Footer>
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed" role="status">
                Showing {items.length.toLocaleString('en-GB')} of{' '}
                {query.data.total.toLocaleString('en-GB')} {noun}
              </Text>
              {query.hasNextPage && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  loading={query.isFetchingNextPage}
                  // Keep focus in the input so the dropdown stays open.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void query.fetchNextPage()}
                >
                  Load more
                </Button>
              )}
            </Group>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}
